import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { getMailHref, getMapHref, getTelHref } from '@/lib/contactLinks';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const footerAddress = '123 Healthcare Blvd, Medical District';
  const footerPhone = '+1 (555) 123-4567';
  const footerEmail = 'contact@dactrha.com';

  return (
    <footer className="border-t border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300" data-testid="main-footer">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/images/logo.png" 
                alt="Dactrha" 
                className="h-10 w-10 rounded-lg object-contain bg-white p-1"
              />
              <span className="text-xl font-bold text-slate-900 dark:text-white">{t('common.appName')}</span>
            </Link>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('common.tagline')}
            </p>
            <div className="flex gap-3">
              <a href="#" className="rounded-full bg-white p-2 text-slate-500 shadow-sm ring-1 ring-slate-200 transition-colors hover:bg-teal-600 hover:text-white dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="#" className="rounded-full bg-white p-2 text-slate-500 shadow-sm ring-1 ring-slate-200 transition-colors hover:bg-teal-600 hover:text-white dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700" aria-label="Twitter">
                <Twitter className="h-4 w-4" />
              </a>
              <a href="#" className="rounded-full bg-white p-2 text-slate-500 shadow-sm ring-1 ring-slate-200 transition-colors hover:bg-teal-600 hover:text-white dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="#" className="rounded-full bg-white p-2 text-slate-500 shadow-sm ring-1 ring-slate-200 transition-colors hover:bg-teal-600 hover:text-white dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700" aria-label="LinkedIn">
                <Linkedin className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">{t('nav.home')}</h3>
            <ul className="space-y-2">
              <li><Link to="/doctors" className="text-sm transition-colors hover:text-teal-600 dark:hover:text-teal-400">{t('nav.doctors')}</Link></li>
              <li><Link to="/specialties" className="text-sm transition-colors hover:text-teal-600 dark:hover:text-teal-400">{t('nav.specialties')}</Link></li>
              <li><Link to="/appointments" className="text-sm transition-colors hover:text-teal-600 dark:hover:text-teal-400">{t('nav.appointments')}</Link></li>
              <li><Link to="/about" className="text-sm transition-colors hover:text-teal-600 dark:hover:text-teal-400">{t('nav.about')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">{t('footer.contactUs')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-teal-400" />
                <a
                  href={getMapHref(footerAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-teal-600 hover:underline dark:hover:text-teal-400"
                >
                  {footerAddress}
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 flex-shrink-0 text-teal-400" />
                <a
                  href={getTelHref(footerPhone)}
                  className="transition-colors hover:text-teal-600 hover:underline dark:hover:text-teal-400"
                >
                  <span dir="ltr">{footerPhone}</span>
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 flex-shrink-0 text-teal-400" />
                <a
                  href={getMailHref(footerEmail)}
                  className="transition-colors hover:text-teal-600 hover:underline dark:hover:text-teal-400"
                >
                  <span dir="ltr">{footerEmail}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-sm transition-colors hover:text-teal-600 dark:hover:text-teal-400">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to="/terms" className="text-sm transition-colors hover:text-teal-600 dark:hover:text-teal-400">{t('footer.termsOfService')}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-slate-200 pt-6 text-center text-sm text-slate-500 dark:border-slate-800 dark:text-slate-500">
          <p>&copy; {currentYear} {t('common.appName')}. {t('footer.allRightsReserved')}.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
